import { Card } from './poiService';

interface CardCache {
  [poiId: string]: {
    cards: Card[];
    timestamp: number;
    loading: boolean;
  };
}

class CardCacheService {
  private cache: CardCache = {};
  private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

  // Check if cache entry is valid
  private isCacheValid(poiId: string): boolean {
    const entry = this.cache[poiId];
    if (!entry) return false;
    
    const now = Date.now();
    return (now - entry.timestamp) < this.CACHE_DURATION;
  }

  // Get cached cards for a POI
  getCachedCards(poiId: string): Card[] | null {
    if (this.isCacheValid(poiId)) {
      return this.cache[poiId].cards;
    }
    return null;
  }

  // Set cards in cache
  setCachedCards(poiId: string, cards: Card[]): void {
    this.cache[poiId] = {
      cards,
      timestamp: Date.now(),
      loading: false
    };
  }

  // Set loading state
  setLoading(poiId: string, loading: boolean): void {
    if (!this.cache[poiId]) {
      this.cache[poiId] = {
        cards: [],
        timestamp: 0,
        loading
      };
    } else {
      this.cache[poiId].loading = loading;
    }
  }

  // Check if currently loading
  isLoading(poiId: string): boolean {
    return this.cache[poiId]?.loading || false;
  }

  // Clear cache for specific POI
  clearCache(poiId: string): void {
    delete this.cache[poiId];
  }

  // Clear cached cards for specific POI (alias for clearCache)
  clearCachedCards(poiId: string): void {
    this.clearCache(poiId);
  }

  // Clear all cache
  clearAllCache(): void {
    this.cache = {};
  }

  // Get cache stats
  getCacheStats(): { totalEntries: number; validEntries: number } {
    const totalEntries = Object.keys(this.cache).length;
    const validEntries = Object.keys(this.cache).filter(
      poiId => this.isCacheValid(poiId)
    ).length;

    return { totalEntries, validEntries };
  }
}

export const cardCacheService = new CardCacheService();
export default cardCacheService;
